import { pollWhileVisible, type components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type ExceptionQueue = components["schemas"]["ExceptionQueue"];
export type FailedEmail = components["schemas"]["FailedEmail"];
export type FailedWebhook = components["schemas"]["FailedWebhook"];
export type BrokenConnector = components["schemas"]["BrokenConnector"];
export type UnfinishedTenant = components["schemas"]["UnfinishedTenant"];

export const QUEUE_PATH = "/api/v1/platform/exceptions";
const COUNT_PATH = "/api/v1/platform/exceptions/count";

/** Everything that failed, or one roastery's share of it for the Health section of its card. */
export function useExceptionQueue(tenantId?: string) {
  const api = useApi();
  return api.query.useQuery("get", QUEUE_PATH, {
    params: { query: tenantId ? { tenantId } : {} },
  });
}

/**
 * The badge. Polled like the roastery's own bell, and for the same reason: it is a number, not a
 * screen — and, since US-18.14, only while the tab is visible, at two minutes rather than one.
 */
export function useExceptionCount() {
  const api = useApi();
  return api.query.useQuery("get", COUNT_PATH, {}, { ...pollWhileVisible(120_000), staleTime: 30_000 });
}

export function useRequeueEmail() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("post", "/api/v1/platform/exceptions/emails/{outboxId}/requeue", {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["get", QUEUE_PATH] });
      void queryClient.invalidateQueries({ queryKey: ["get", COUNT_PATH] });
    },
  });
}

export function useDeleteEmptyTenant() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("delete", "/api/v1/platform/tenants/{id}", {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["get", QUEUE_PATH] });
      void queryClient.invalidateQueries({ queryKey: ["get", COUNT_PATH] });
      void queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/platform/tenants/overview"] });
    },
  });
}
